<template>
  <TransitionProjectModal
    v-model="showModal"
    :projectData="setProjectValue"
    :state="toState"
    @close="revertDragIfCanceled"
  />
  <HistoryModal
    v-model="showHistory"
    :idProject="projectSeleccionado"
    @close="closeHistory"
  />

  <div class="board">
    <div class="projects" ref="projectsEl">
      <div
        v-for="col in columns"
        :key="col.key"
        class="lane"
        :data-col-key="col.key"
        :data-col-title="col.title"
      >
        <h3 class="lane-title">{{ col.title }}</h3>

        <div class="lane-body">
          <VueDraggable
            v-model="col.list"
            item-key="id"
            :animation="150"
            ghostClass="ghost"
            group="projects"
            :data-col-key="col.key"
            :data-col-title="col.title"
            @end="onChange"
            style="min-height: 200px"
          >
            <Card
              v-for="item in col.list"
              :key="item.id"
              :class="getPriorityClass(item)"
              @mousemove="setProject(item)"
              @click="onCardClick($event, item.id)"
            >
              <div class="inline">
                <h6>{{ item.project_code }}_{{ item.name }}</h6>
                <div class="inlineRight">
                  <button
                    :style="{
                      backgroundColor: projectType(
                        item.project_type_catalog_name
                      ).color,
                      color: 'black',
                    }"
                    @click.stop
                    class="btn btn-outline-light right"
                    v-tooltip.top="item.project_type_catalog_name"
                  >
                    {{ projectType(item.project_type_catalog_name).abbr }}
                  </button>

                  <div class="dropdown dropdown-float">
                    <button
                      :disabled="
                        item &&
                        ['No Autorizado', 'Cancelado', 'Terminado'].includes(
                          item.state
                        )
                      "
                      class="btn btn-outline-light rightPriority"
                      type="button"
                      id="priorityDropdown"
                      data-bs-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      {{ typeOfPriority(item.priority) }}
                    </button>
                    <div
                      class="dropdown-menu"
                      aria-labelledby="priorityDropdown"
                    >
                      <a
                        class="dropdown-item"
                        @click="updatePriorityProject(item.id, 0)"
                        >Muy prioritario</a
                      >
                      <a
                        class="dropdown-item"
                        @click="updatePriorityProject(item.id, 1)"
                        >Prioritario</a
                      >
                      <a
                        class="dropdown-item"
                        @click="updatePriorityProject(item.id, 2)"
                        >Moderado</a
                      >
                      <a
                        class="dropdown-item"
                        @click="updatePriorityProject(item.id, 3)"
                        >Menos prioritario</a
                      >
                    </div>
                  </div>
                </div>
              </div>

              <p>ETA: {{ item.eta }}</p>
              <div class="inline">
                <p>RAPE: {{ item.rape["name"] }}</p>
                <div class="redonder">
                  <button
                    class="btn btn-outline-light"
                    @click="openHistory(item.id)"
                  >
                    Historial
                  </button>
                </div>
              </div>
            </Card>
          </VueDraggable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch, ref } from "vue";
import { VueDraggable } from "vue-draggable-plus";
import { useStore } from "vuex";
import Card from "./Card.vue";
import HistoryModal from "./HistoryModal.vue";
import TransitionProjectModal from "./TransitionProjectModal.vue";

const store = useStore();
const projects = computed(() => store.state.projects.projects);

const columns = reactive([
  { key: "requirement", title: "Requerimiento", list: [] },
  { key: "start_proposal", title: "Propuesta", list: [] },
  { key: "work_in_progress", title: "En Ejecución", list: [] },
  { key: "finish_work", title: "Autorización Cliente", list: [] },
  { key: "client_approves", title: "Garantía", list: [] },
]);

watch(projects, () => {
  columns[0].list = filterProjects(["Requerimiento"], false);
  columns[1].list = filterProjects(["Propuesta"], false);
  columns[2].list = filterProjects(["En Ejecución"], false);
  columns[3].list = filterProjects(["Autorización del Cliente"], false);
  columns[4].list = filterProjects(["Garantía"], false);
});

const filterProjects = (states, sortByDateOnly, limit) => {
  const filteredProjects = projects.value.filter((item) =>
    states.includes(item.state)
  );

  const sortedProjects = filteredProjects.sort((a, b) => {
    if (sortByDateOnly) {
      return new Date(b.eta) - new Date(a.eta);
    } else {
      const priorityOrder = ["vip", "plan", "entrust", "avoid"];
      const priorityA = priorityOrder.indexOf(a.priority);
      const priorityB = priorityOrder.indexOf(b.priority);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      } else {
        return new Date(b.eta) - new Date(a.eta);
      }
    }
  });
  return limit ? sortedProjects.slice(0, limit) : sortedProjects;
};

function onCardClick(e, id) {
  if (
    e.target.closest(
      '.dropdown, .dropdown-menu, .btn, a, input, textarea, select, [role="button"]'
    )
  ) {
    return;
  }
  openHistory(id);
}

const toState = ref();

const dragSnapshot = ref(null);

function onChange(evt) {
  dragSnapshot.value = {
    fromKey: evt.from?.dataset?.colKey,
    toKey: evt.to?.dataset?.colKey,
    fromIndex: evt.oldIndex,
    toIndex: evt.newIndex,
  };

  if (dragSnapshot.value.fromKey === dragSnapshot.value.toKey) return

  const specialTransitions = {
    "finish_work->work_in_progress": "client_not_approves",
    "client_approves->work_in_progress": "incidence_found",
  };

  const transitionKey = `${dragSnapshot.value.fromKey}->${dragSnapshot.value.toKey}`;

  toState.value = specialTransitions[transitionKey] || dragSnapshot.value.toKey;
  showModal.value = true;
}

function revertDragIfCanceled() {
  const snap = dragSnapshot.value;
  if (!snap) return;

  const fromCol = columns.find((c) => c.key === snap.fromKey);
  const toCol = columns.find((c) => c.key === snap.toKey);

  const [moved] = toCol.list.splice(snap.toIndex, 1);
  fromCol.list.splice(snap.fromIndex, 0, moved);

  dragSnapshot.value = null;
  closeModal();
}

function getPriorityClass(item) {
  const priorities = {
    vip: "priority-one",
    plan: "priority-two",
    entrust: "priority-three",
    avoid: "priority-four",
  };
  return priorities[item.priority] || "";
}

function typeOfPriority(statusName) {
  const types = {
    vip: "1",
    plan: "2",
    entrust: "3",
    avoid: "4",
  };
  return types[statusName] || "";
}

function projectType(statusName) {
  const types = {
    "Cliente con Factura": { abbr: "CCF", color: "#FFF" },
    "Cliente Facturado": { abbr: "CF", color: "#548dd4" },
    "Intercambio de Recurso Asignado": { abbr: "IRA", color: "#f79646" },
    "Proyecto Cancelado": { abbr: "PC", color: "#999999" },
    "Proyecto Interno": { abbr: "PI", color: "#ffff00" },
    "Proyecto Sin Cobro": { abbr: "PSC", color: "#f79646" },
    "Recurso Asignado": { abbr: "RA", color: "#99cc00" },
    Soporte: { abbr: "S", color: "#c27ba0" },
  };

  return types[statusName] || { abbr: "", color: "transparent" };
}

async function fetchProjects() {
  store.dispatch("app/showLoading");
  try {
    await store.dispatch("fetchProjects");
  } finally {
    store.dispatch("app/hideLoading");
  }
}

async function updatePriorityProject(idProject, numberPriority) {
  store.dispatch("app/showLoading");
  try {
    const data = { project: { priority: numberPriority } };
    await store.dispatch("updatePriorityProject", { id: idProject, data });
    await fetchProjects();
  } finally {
    store.dispatch("app/hideLoading");
  }
}

const showHistory = ref(false);
const projectSeleccionado = ref(null);

function openHistory(project) {
  projectSeleccionado.value = project;
  showHistory.value = true;
}

function closeHistory() {
  showHistory.value = false;
}

const showModal = ref(false);

const closeModal = () => {
  showModal.value = false;
};

const setProjectValue = ref({});
const setProject = (item) => {
  setProjectValue.value = item;
};
</script>

<style scoped>
.board {
  --top-offset: 96px;
  height: calc(100vh - var(--top-offset));
  display: flex;
  flex-direction: column;
  min-height: 420px;
}

.projects {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  gap: 12px;
  padding: 8px 12px 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.lane {
  flex: 0 0 auto;
  width: clamp(220px, 26vw, 320px);
  max-width: 420px;
  min-width: 240px;

  display: flex;
  flex-direction: column;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  scroll-snap-align: start;
  overflow: hidden;
}

.lane-title {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 0;
  padding: 10px 12px;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  backdrop-filter: blur(4px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.lane-body {
  min-height: 0;
  max-height: calc(100% - 44px);
  overflow-y: auto;
  padding: 10px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.lane-body .card,
.lane-body Card {
  display: block;
  margin-bottom: 10px;
  border-radius: 14px;
  padding: 10px;
}

.inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inlineRight {
  margin-left: auto;
  display: flex;
  gap: 6px;
  align-items: center;
}

.right,
.rightPriority {
  padding: 4px 8px !important;
  font-size: 1rem !important;
  line-height: 1 !important;
}

.lane-body h6 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ghost {
  opacity: 0.5;
}

@media (max-width: 480px) {
  .projects {
    gap: 10px;
    padding: 8px 8px 14px;
  }
  .lane {
    width: clamp(240px, 82vw, 300px);
    border-radius: 14px;
  }
  .lane-title {
    font-size: 0.95rem;
    padding: 8px 10px;
  }
  .lane-body {
    padding: 8px;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .lane {
    width: clamp(280px, 28vw, 360px);
  }
}

@media (min-width: 1400px) {
  .lane {
    width: clamp(320px, 24vw, 420px);
  }
}

.priority-one {
  background-color: #22070d;
}

.priority-two {
  background-color: #3b264d;
}

.priority-three {
  background-color: #26304d;
}

.priority-four {
  background-color: #26454d;
}

:deep(.projects::-webkit-scrollbar) {
  height: 12px;
}

:deep(.projects::-webkit-scrollbar-thumb) {
  background: #ffffff66;
  border-radius: 10px;
  border: 3px solid transparent;
  background-clip: content-box;
}

:deep(.projects::-webkit-scrollbar-track) {
  background: transparent;
}

:deep(.projects) {
  scrollbar-width: auto;
  scrollbar-color: #ffffff66 transparent;
}
</style>
